import MapboxGL from './mapbox-gl-loader.es6';

/**
 * Helpers for Google Maps
 */
export const MapHelpers = {
    /**
     * Parse a view box string
     * @param {String} box Expected format is "BOX(-3.9990234375 43.19716728250127, 7.55859375 50.764259357116465)"
     * @return {Object|null}
     */
    parseBox(box) {
        let bounds;
        box.replace(
            /^BOX\(([-0-9.]*) ([-0-9.]*), ?([-0-9.]*) ([-0-9.]*)\)$/i,
            (all, left, bottom, right, top) => {
                bounds = {
                    left,
                    bottom,
                    right,
                    top,
                    sw: {
                        lat: bottom,
                        lng: left,
                    },
                    ne: {
                        lat: top,
                        lng: right,
                    },
                };
            },
        );
        return bounds;
    },

    /**
     * Returns a LatLngBounds object from a view box string
     * @param {string} boxstr Expected format is "BOX(-3.9990234375 43.19716728250127, 7.55859375 50.764259357116465)"
     */
    boundsFromBox(boxstr) {
        const box = this.parseBox(boxstr);
        if (box && MapboxGL) {
            const sw = new MapboxGL.LngLat(box.sw.lng, box.sw.lat);
            const ne = new MapboxGL.LngLat(box.ne.lng, box.ne.lat);
            return new MapboxGL.LngLatBounds(sw, ne);
        }
        return null;
    },

    /**
     * Helper to get a bounds string representation
     *
     * @param {mapboxgl.LngLatBounds} bounds
     * @return {string}
     */
    getBoundsString(bounds) {
        const southWestBound = bounds.getSouthWest();
        const northEastBound = bounds.getNorthEast();
        const boundsString = `${southWestBound.lat},${southWestBound.lng},${northEastBound.lat},${northEastBound.lng}`;
        return boundsString;
    },

    /**
     *
     * @param {Object|Array} coordinates {lat: Number, lng: Number} or [lat<Number>, lng<Number>]
     * @return {string}
     */
    coordinatesToString(coordinates) {
        if (coordinates instanceof Array) {
            return `${coordinates[0]},${coordinates[1]}`;
        }
        return `${coordinates.lng},${coordinates.lat}`;
    },

    /**
     * Calculate distance between 2 points
     * @param {Number} lng1 Longitude of first point
     * @param {Number} lat1 Latitude of first point
     * @param {Number} lng2 Longitude of second point
     * @param {Number} lat2 Latitude of second point
     * @returns {Number}
     */
    calculateDistance(lng1, lat1, lng2, lat2) {
        const R = 6371e3;
        const lat1Rad = lat1.toRad();
        const lat2Rad = lat2.toRad();
        const deltaLat = (lat2 - lat1).toRad();
        const deltaLng = (lng2 - lng1).toRad();

        const a =
            Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },

    /**
     * Extract 80% of closest features (past sales) from a point
     * @param {Object} features
     * @param {{lat: Number, lng: Number}} fromPoint
     * @return {Array<Object>} Colletion of features
     */
    extractClosestFeatures(features, fromPoint) {
        const sortedFeatures = features
            .filter(
                feature =>
                    typeof feature.properties.coordinates !== 'undefined' &&
                    feature.properties.coordinates !== null,
            )
            .map(feature => {
                const { coordinates } = feature.properties;

                // eslint-disable-next-line no-param-reassign
                feature.distance = MapHelpers.calculateDistance(
                    fromPoint.lat,
                    fromPoint.lng,
                    coordinates.lat,
                    coordinates.lng,
                );
                return feature;
            })
            .sort((a, b) => a.distance - b.distance);

        const closestFeatures = sortedFeatures.slice(
            0,
            Math.ceil((sortedFeatures.length * 80) / 100),
        );
        return closestFeatures;
    },

    /**
     * Get bounds wrapping a features list
     */
    getBoundsForFeatures(features) {
        const bounds = new MapboxGL.LngLatBounds();
        for (let i = 0; i < features.length; i++) {
            bounds.extend(
                new MapboxGL.LngLat(
                    features[i].properties.coordinates.lng,
                    features[i].properties.coordinates.lat,
                ),
            );
        }
        return bounds;
    },

    placeIsAddress(place) {
        return place && place.geotype_label === 'address';
    },

    placeIsStreet(place) {
        return place && place.geotype_label === 'street';
    },

    /**
     * Determines if coordinates are in a given bounds
     *
     * @param {{lat: Number, lng: Number}} coordinates
     * @param {MapboxGL.LngLatBounds} bounds
     */
    isCoordinatesInBounds(coordinates, bounds) {
        const { lat, lng } = coordinates;
        const [sw, ne] = [bounds.getSouthWest(), bounds.getNorthEast()];
        return lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng;
    },

    /**
     * Generate a FeatureCollection object from a given geometry
     *
     * @param {GeoJSON} geometry
     */
    getFeatureCollectionFromGeometry(geometry) {
        return {
            type: 'FeatureCollection',
            features: geometry ? [{ type: 'Feature', geometry }] : [],
        };
    },
};
